import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import { Button } from "react-bootstrap";
import * as config from "config";
import { withApiContext, withLabelsContext, withUserContext, withWindowContext } from "context";
import Loader from "components/Spinners/Loader";

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.bodyBg || "#f2f2f2"};
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 600;
  color: ${({ theme }) => theme.color1 || "#272D36"};
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ActionButton = styled(Button)`
  && {
    min-width: 100px;
    border-radius: 999px;
    font-weight: 600;
    border: none;
    background: ${({ $variant, theme }) => {
      if ($variant === "primary") return theme.primary;
      if ($variant === "danger") return "#E9505B";
      return theme.color7 || "#ffffff";
    }};
    color: ${({ $variant, theme }) => {
      if ($variant === "ghost") return theme.primary;
      return theme.button?.primary?.text || "#ffffff";
    }};
    border: ${({ $variant, theme }) =>
      $variant === "ghost" ? `1px solid ${theme.primary}` : "none"};
  }

  &&:hover,
  &&:focus,
  &&:active {
    background: ${({ $variant, theme }) => {
      if ($variant === "primary") {
        return theme.button?.primary?.backgroundHover || theme.primaryDarken || theme.primary;
      }
      if ($variant === "danger") return "#D63E49";
      return theme.color6 || "#E8EEF0";
    }};
    color: ${({ $variant, theme }) => ($variant === "ghost" ? theme.primary : "#ffffff")};
  }
`;

const Body = styled.div`
  background: ${({ theme }) => theme.color7 || "#ffffff"};
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const QuestionNav = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const QuestionChip = styled.button`
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ $active, theme }) => ($active ? theme.color2 : theme.color6 || "#E8EEF0")};
  color: ${({ $active }) => ($active ? "#ffffff" : "#4B5563")};
`;

const QuestionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: ${({ theme }) => theme.color1 || "#272D36"};
  line-height: 1.4;
`;

const QuestionTitleLight = styled.span`
  font-weight: 400;
`;

const TagsRow = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const Tag = styled.span`
  padding: 6px 16px;
  border-radius: 999px;
  background: ${({ theme }) => theme.color6 || "#E8EEF0"};
  color: ${({ theme }) => theme.primary};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
`;

const UploadCard = styled.div`
  border: 2px dashed ${({ theme }) => theme.primary};
  border-radius: 16px;
  padding: 32px;
  background: rgba(32, 166, 210, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 16px;
`;

const UploadTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.color1 || "#272D36"};
  margin: 0;
`;

const UploadSub = styled.span`
  color: ${({ theme }) => theme.color2 || "#001B4A"};
  font-size: 12px;
`;

const PrimaryAction = styled(Button)`
  && {
    border-radius: 999px;
    padding: 10px 24px;
    background: ${({ theme }) => theme.color2};
    border: none;
    font-weight: 600;
  }
`;

const RichTextArea = styled.textarea`
  width: 100%;
  min-height: 260px;
  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color5 || "#E0E0E0"};
  padding: 16px;
  resize: vertical;
  font-size: 14px;
  outline: none;
  box-shadow: inset 0 2px 6px rgba(15, 23, 42, 0.06);
`;

const FooterNav = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  align-items: center;
`;

const NavButton = styled(Button)`
  && {
    min-width: 80px;
    border-radius: 999px;
    background: ${({ theme }) => theme.primary};
    border: none;
    font-weight: 600;
    color: ${({ theme }) => theme.button?.primary?.text || "#ffffff"};
  }
`;

const StatusText = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme, $variant }) =>
    $variant === "error" ? "#E9505B" : theme.color2 || "#001B4A"};
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  gap: 16px;
`;

const LoadingText = styled.div`
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
  text-align: center;
`;

const LoadingSubtext = styled.div`
  color: #ffffff;
  font-size: 14px;
  text-align: center;
  opacity: 0.9;
`;

const NO_DATA_TAG = "No criteria available";

const adaptQuestionForSummary = (question) => {
  if (!question) {
    return null;
  }

  const questionId = question.question_id ?? question.id ?? "";
  const rubricData = Array.isArray(question?.rubric_data) ? question.rubric_data : [];

  return {
    id: questionId,
    text: question.question_text ?? question.title ?? question.text ?? "",
    criteria: rubricData.map((criteria, idx) => ({
      id: `${questionId || "criteria"}-${idx + 1}`,
      title: criteria.criteria ?? criteria.title ?? criteria.name ?? "",
      heading: criteria.heading || "",
      description: Array.isArray(criteria.categories)
        ? criteria.categories
            .map((category) => {
              const label = category.category ?? category.level ?? "";
              return category.instruction ? `${label}: ${category.instruction}` : label;
            })
            .filter(Boolean)
            .join(" | ")
        : "",
      rating: criteria.rating ?? "",
      marks: Number(
        criteria.marks ??
          criteria.score ??
          criteria.points ??
          question.total_marks ??
          question.totalMarks ??
          0
      ),
    })),
  };
};

const deriveQuestionPreview = (question) => {
  if (!question) {
    return {
      title: "",
      tags: [],
    };
  }

  const title =
    question.question_text ||
    question.title ||
    question.text ||
    question.description ||
    "Question details";

  const explicitTags =
    (Array.isArray(question.tags) && question.tags.length ? question.tags : null) ||
    (Array.isArray(question.rubric_data) && question.rubric_data.length
      ? question.rubric_data
          .map((criteria) => criteria.criteria || criteria.title || criteria.name)
          .filter(Boolean)
      : null) ||
    (Array.isArray(question.criteria) && question.criteria.length
      ? question.criteria
          .map((criteria) => criteria.title || criteria.name || criteria.heading)
          .filter(Boolean)
      : null);

  return {
    title,
    tags: explicitTags && explicitTags.length ? explicitTags : [],
  };
};

const buildCriteriaTags = (question) => {
  if (!question) {
    return [];
  }

  if (Array.isArray(question.rubric_data) && question.rubric_data.length) {
    return question.rubric_data.map(
      (criteria, idx) =>
        criteria.criteria || criteria.title || criteria.name || `Criteria ${idx + 1}`
    );
  }

  if (Array.isArray(question.criteria) && question.criteria.length) {
    return question.criteria.map(
      (criteria, idx) => criteria.title || criteria.name || criteria.heading || `Criteria ${idx + 1}`
    );
  }

  return [];
};

const StudentRubricAttempt = (props) => {
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { ctx_api, ctx_window } = props;

  const rubrics = location.state?.rubrics;
  console.log(rubrics)
  const [rubricQuestions, setRubricQuestions] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [fetchError, setFetchError] = useState("");
  const [activeQuestionIndex, setActiveQuestionIndex] = useState(0);
  const [responsesByQuestionId, setResponsesByQuestionId] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const goToQuestion = (idx) => {
    if (!Array.isArray(availableQuestions) || !availableQuestions.length) {
      return;
    }
    setActiveQuestionIndex((prev) => {
      if (idx === undefined || idx === null) {
        return prev;
      }
      if (idx < 0) {
        return 0;
      }
      if (idx >= availableQuestions.length) {
        return availableQuestions.length - 1;
      }
      return idx;
    });
  };

  const handlePrevQuestion = () => {
    if (activeQuestionIndex <= 0) return;
    goToQuestion(activeQuestionIndex - 1);
  };

  const handleNextQuestion = () => {
    if (activeQuestionIndex >= availableQuestions.length - 1) return;
    goToQuestion(activeQuestionIndex + 1);
  };

const handleCancel = () => {
  if (history.length > 1) {
    history.goBack();
  } else {
    history.push(config.ROUTES.StudentRubrics);
  }
};


  const testId =
    rubrics?.id ??
    rubrics?.testId ??
    location.state?.testId ??
    location.state?.testid ??
    rubrics?.rubrics_assessment_id ??
    null;
console.log(rubrics)
  const isInProgress = rubrics?.rubric_status === "In Progress";

  useEffect(() => {
    if (!ctx_api || !testId) {
      return;
    }

    let isMounted = true;
    const fetchRubricsQuestions = async () => {
      setIsLoadingQuestions(true);
      setFetchError("");
      try {
        const { rsp } = await ctx_api.privateRequest({
          cmd: "get_rubrics_student_assessment_details",
          args: { testId: String(testId) },
        });
        if (!isMounted) {
          return;
        }
        const payload = Array.isArray(rsp) ? rsp : [];
        const assessment = payload[0] || {};
        const questions = Array.isArray(assessment.questions) ? assessment.questions : [];
        setRubricQuestions(questions);

        // If in progress, fetch saved responses
        if (isInProgress) {
          try {
            const { rsp: savedRsp } = await ctx_api.privateRequest({
              cmd: "get_rubrics_student_saved_responses",
              args: { rubric_assessment_id: String(testId) },
            });
            if (!isMounted) return;
            
            const savedData = Array.isArray(savedRsp) ? savedRsp[0] : savedRsp;
            if (savedData?.rubric_details?.questions) {
              const savedResponses = {};
              savedData.rubric_details.questions.forEach((q) => {
                const qid = q.question_id || q.id;
                if (qid && q.response) {
                  savedResponses[qid] = q.response;
                }
              });
              setResponsesByQuestionId(savedResponses);
            }
          } catch (savedError) {
            console.error("Failed to load saved responses", savedError);
          }
        }
      } catch (error) {
        if (!isMounted) {
          return;
        }
        // eslint-disable-next-line no-console
        console.error("Failed to load rubric assessment details", error);
        setFetchError("Unable to load rubric questions right now.");
        setRubricQuestions([]);
      } finally {
        if (isMounted) {
          setIsLoadingQuestions(false);
        }
      }
    };

    fetchRubricsQuestions();

    return () => {
      isMounted = false;
    };
  }, [ctx_api, testId, isInProgress]);

  const availableQuestions = useMemo(() => {
    if (rubricQuestions.length) {
      return rubricQuestions;
    }
    if (Array.isArray(rubrics?.questions) && rubrics.questions.length) {
      return rubrics.questions;
    }
    return [];
  }, [rubricQuestions, rubrics]);

  useEffect(() => {
    setActiveQuestionIndex((prev) =>
      prev >= availableQuestions.length ? 0 : prev
    );
  }, [availableQuestions.length]);

  const activeQuestion = availableQuestions[activeQuestionIndex] || null;

  const question = useMemo(
    () => deriveQuestionPreview(activeQuestion),
    [activeQuestion]
  );

  const criteriaTags = useMemo(
    () => buildCriteriaTags(activeQuestion),
    [activeQuestion]
  );

  const hasAnyResponse = useMemo(() => {
    const values = Object.values(responsesByQuestionId || {});
    return values.some((v) => (v || "").trim() !== "");
  }, [responsesByQuestionId]);

  const handleSubmit = () => {
    if (!availableQuestions.length) {
      ctx_window?.alert?.("No questions available to submit.");
      return;
    }

    const questions = availableQuestions
      .map((question, index) => {
        const normalized = adaptQuestionForSummary(question);
        if (!normalized) {
          return null;
        }
        return {
          id: normalized.id || String(index + 1),
          text: normalized.text || `Question ${index + 1}`,
          criteria: Array.isArray(normalized.criteria) ? normalized.criteria : [],
        };
      })
      .filter(Boolean);

    if (!questions.length) {
      ctx_window?.alert?.("No rubric details available to submit.");
      return;
    }

    history.push({
      pathname: config.ROUTES.StudentRubricSummary,
      state: {
        rubrics,
        summary: {
          testName: rubrics?.name || "",
          subject: rubrics?.currTitle || rubrics?.currName || rubrics?.subject || "",
          startDate: rubrics?.new_start_date || "",
          endDate: rubrics?.new_due_date || "",
          attempt: rubrics?.attempt || rubrics?.attempts || 1,
          totalMarks: rubrics?.totalMarks || rubrics?.score || 0,
          questions,
        },
      },
    });
  };

  const persistStudentAttempt = async ({ isSubmit }) => {
    if (isSubmitting) return null; // Prevent multiple submissions

    const memberId = props?.ctx_user?.user?.id;
    const rubricAssessmentId = testId;
    const in_progress = isSubmit ? 0 : 1;
    const attended = isSubmit ? 1 : 0;

    const rubric_details = {
      rubric_assessment_id: rubricAssessmentId,
      name: rubrics?.name || "",
      questions: (availableQuestions || []).map((q, idx) => {
        const qid =
          q?.question_id ??
          q?.id ??
          (adaptQuestionForSummary(q)?.id || String(idx + 1));
        const rubric_data = Array.isArray(q?.rubric_data)
          ? q.rubric_data
          : Array.isArray(q?.rubricData)
          ? q.rubricData
          : [];
        const criteria_details = Array.isArray(q?.criteria_details)
          ? q.criteria_details
          : Array.isArray(q?.criteriaDetails)
          ? q.criteriaDetails
          : [];

        return {
          rubric_id: q?.rubric_id ?? q?.rubricId ?? null,
          question_id: qid,
          rubric_data,
          rubric_type: q?.rubric_type ?? q?.rubricType ?? "",
          total_marks: q?.total_marks ?? q?.totalMarks ?? null,
          question_text:
            q?.question_text ?? q?.title ?? q?.text ?? `Question ${idx + 1}`,
          response: responsesByQuestionId[qid] || "",
          question_order: q?.question_order ?? q?.questionOrder ?? idx + 1,
          criteria_details,
        };
      }),
    };

    const total = rubric_details.questions.length || 0;
    const answered = rubric_details.questions.filter((q) => (q.response || "").trim() !== "").length;
    const unanswered = total - answered;
    const progress = total ? Math.round((answered / total) * 100) : 0;

    if (isSubmit) {
      setIsSubmitting(true);
    }

    try {
      const { rsp } = await ctx_api.privateRequest({
        cmd: "insert_update_student_rubrics_assessment",
        args: {
          rubric_assessment_id: String(rubricAssessmentId),
          rubric_details,
          in_progress,
          attended,
          progress,
          attempted_count: answered,
          unattempted_count: unanswered,
          total_questions: total,
          rubric_assessment_name: rubrics?.name || "",
          end_time: isSubmit ? new Date().toISOString() : null,
        },
      });
      if (isSubmit) {
        ctx_window?.alert?.("Submission successful.");
      } else {
        ctx_window?.alert?.("Progress saved.");
      }
      return rsp;
    } catch (e) {
      ctx_window?.alert?.("Failed to save. Please try again.");
      return null;
    } finally {
      if (isSubmit) {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <Page>
      <Header>
        <Title>{rubrics?.name || "Number of sources"}</Title>
        <HeaderActions>
          <ActionButton $variant="ghost" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </ActionButton>
          <ActionButton
            $variant="primary"
            onClick={() => persistStudentAttempt({ isSubmit: false })}
            disabled={!availableQuestions.length || !hasAnyResponse || isSubmitting}
          >
            Save
          </ActionButton>
          <ActionButton
            $variant="danger"
            onClick={() => {
              persistStudentAttempt({ isSubmit: true }).then(() => handleSubmit());
            }}
            disabled={!availableQuestions.length || !hasAnyResponse || isSubmitting}
          >
            {isSubmitting ? "Submitting..." : "Submit"}
          </ActionButton>
        </HeaderActions>
      </Header>

      {(isLoadingQuestions || fetchError) && (
        <StatusText $variant={fetchError ? "error" : "info"}>
          {fetchError || "Loading rubric questions..."}
        </StatusText>
      )}

      <Body>
        <QuestionNav>
          {!availableQuestions.length ? (
            <StatusText>No questions available.</StatusText>
          ) : (
            availableQuestions.map((_, idx) => (
              <QuestionChip
                key={`question-${idx}`}
                type="button"
                $active={idx === activeQuestionIndex}
                onClick={() => goToQuestion(idx)}
              >
                {idx + 1}
              </QuestionChip>
            ))
          )}
        </QuestionNav>

        <QuestionTitle>
          {availableQuestions.length
            ? `Question ${activeQuestionIndex + 1} `
            : "No question selected"}
          {availableQuestions.length ? (
            <QuestionTitleLight>{question.title}</QuestionTitleLight>
          ) : null}
        </QuestionTitle>

        <TagsRow>
          {availableQuestions.length &&
          Array.isArray(criteriaTags) &&
          criteriaTags.length > 0 ? (
            criteriaTags.map((tag, idx) => <Tag key={`${tag}-${idx}`}>{tag}</Tag>)
          ) : availableQuestions.length ? (
            <Tag key="no-criteria">{NO_DATA_TAG}</Tag>
          ) : null}
        </TagsRow>

        <ContentGrid>
          <UploadCard>
            <UploadTitle>Drag &amp; Drop file here</UploadTitle>
            <UploadSub>or browse your file here</UploadSub>
            <PrimaryAction type="button" disabled={!availableQuestions.length}>
              View Rubrics
            </PrimaryAction>
          </UploadCard>
          <RichTextArea
            theme={theme}
            placeholder={
              availableQuestions.length ? "Type your answer here..." : "No question selected."
            }
            disabled={!availableQuestions.length}
            value={
              (() => {
                if (!availableQuestions.length) return "";
                const normalized = adaptQuestionForSummary(activeQuestion);
                const qid =
                  normalized?.id ||
                  activeQuestion?.question_id ||
                  activeQuestion?.id ||
                  String(activeQuestionIndex + 1);
                return responsesByQuestionId[qid] ?? "";
              })()
            }
            onChange={(e) => {
              const normalized = adaptQuestionForSummary(activeQuestion);
              const qid =
                normalized?.id ||
                activeQuestion?.question_id ||
                activeQuestion?.id ||
                String(activeQuestionIndex + 1);
              setResponsesByQuestionId((prev) => ({ ...prev, [qid]: e.target.value }));
            }}
          />
        </ContentGrid>

        <FooterNav>
          <NavButton
            variant="light"
            onClick={handlePrevQuestion}
            disabled={activeQuestionIndex === 0 || !availableQuestions.length}
          >
            Prev
          </NavButton>
          <NavButton
            variant="light"
            onClick={handleNextQuestion}
            disabled={
              !availableQuestions.length ||
              activeQuestionIndex === availableQuestions.length - 1
            }
          >
            Next
          </NavButton>
        </FooterNav>
      </Body>
      
      {isSubmitting && (
        <LoadingOverlay>
          <Loader width={50} padding="0" />
          <LoadingText>Submitting Assessment...</LoadingText>
          <LoadingSubtext>Please wait while we process your submission</LoadingSubtext>
        </LoadingOverlay>
      )}
    </Page>
  );
};


export default withApiContext(
  withUserContext(withLabelsContext(withWindowContext(StudentRubricAttempt)))
);
